import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import type { ExternalTimeOffDiff } from "../utils/models";
import { NURSE_QUERY_KEY, useInvalidateNurseQueries } from "./useInvalidateNurseQueries";

export const useSyncNursePto = (nurseId: number | undefined) => {
	const { showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const invalidateNurseQueries = useInvalidateNurseQueries();

	return useMutation({
		mutationFn: async () => {
			if (!nurseId || !chosenDepartment?.department_id) return;
			const response = await backendService.syncNursePto({
				nurseId,
				departmentId: chosenDepartment.department_id,
			});
			return response.data;
		},
		onSuccess: (data) => {
			if (!data) return;
			const count = countDiff(data);
			showSnackbar(t("pto.sync.nurse.success", { count }), "success");
			track("Integrations - Nurse PTO synced", { nurse_id: nurseId ?? null, count });
			// The profile's PTO list and its weeks both change (the sync also trims or removes shifts the new
			// time off overlaps); refresh them, plus any time-off lists that may show this nurse.
			invalidateNurseQueries(NURSE_QUERY_KEY);
			queryClient.invalidateQueries({ queryKey: ["time_off"] });
			queryClient.invalidateQueries({ queryKey: ["pto-costs"] });
		},
		onError: (error) => showErrorSnackbar(t("pto.sync.nurse.failed"), { action: "external_nurse_pto_sync" }, error),
	});
};

const countDiff = (diff: ExternalTimeOffDiff): number => diff.to_insert.length + diff.to_update.length + diff.to_delete.length;
